import { html, css, LitElement } from 'lit';
import { DomModule } from '@polymer/polymer/lib/elements/dom-module.js';
import { Map, Marker, TileLayer, Icon } from 'leaflet/dist/leaflet-src.esm.js';
import '@cbar/cbar-shared-styles';
import { leafletStyles } from './leaflet-styles.js';

const themeTemplate = DomModule.import('cbar-shared-styles', 'template');
const sharedThemeElement = themeTemplate?.content?.firstElementChild;

if (!sharedThemeElement) {
  console.warn('The theme template or its first element child is missing.');
}

const sharedTheme = sharedThemeElement?.textContent ?? '';

export class CbarMap extends LitElement {
  static get styles() {
    return css`
      :host {
      }
    `;
  }

  static get properties() {
    return {
      /**
       * Object for dictating where the map should be centered, can be used to indicate where the target to display
       * is located
       *
       * example:
       *
       * {
       *   title: "Testing Location",
       *   address: "96 Phillip Street, Parramatta, NSW, Australia, 2150"
       *   lat: 51.505,
       *   lng: -0.09
       * }
       */
      location: { type: Object },
      /**
       * Array for markers to display on the map
       */
      markers: { type: Array },
      /**
       * Theming style for the map
       */
      theme: { type: String },
      /**
       * Used if we are adding extra markers to the map object
       */
      locationIsUserIcon: { type: Boolean },
      /**
       * Map object
       */
      map: { type: Object },
    };
  }

  constructor() {
    super();
    this.location = {};
    this.markers = [];
    this.theme = '';
    this.locationIsUserIcon = false;
    this.map = {};
  }

  get location() {
    return this._location;
  }

  get markers() {
    return this._markers;
  }

  get theme() {
    return this._theme;
  }

  get map() {
    return this._map;
  }

  get locationIsUserIcon() {
    return this._locationIsUserIcon;
  }

  get mapDiv() {
    if (!this.__map) {
      this.__map = this.shadowRoot.querySelector('#map');
    }
    return this.__map;
  }

  set location(location) {
    this._location = location;

    if (location && location.lat && location.lng) {
      if (Object.keys(this.map).length === 0) {
        requestAnimationFrame(() => {
          this.initializeMap(location);
        });
      } else {
        this.updateMapCenter(location);
      }
    }

    this.requestUpdate();
  }

  set markers(markers) {
    this._markers = markers;
    this.requestUpdate();
  }

  set theme(theme) {
    this._theme = theme;
    this.requestUpdate();
  }

  set map(map) {
    this._map = map;
    this.requestUpdate();
  }

  set locationIsUserIcon(locationIsUserIcon) {
    this._locationIsUserIcon = locationIsUserIcon;
    this.requestUpdate();
  }

  render() {
    return html`
      <style>
        ${sharedTheme} ${leafletStyles} #map {
          height: var(--cbar-map-height, 200px);
        }
      </style>
      <div class="map-wrapper">
        <div
          id="map"
          class="sub-text"
          style="color: black; z-index: 1; background: lightgrey; display: flex; justify-content: center; align-items: center; border-radius: 5px"
        ></div>
      </div>
    `;
  }

  /**
   * returns a new icon depending on whether it indicates where we are, or where a location is
   * @param isCenterIcon
   * @return {*}
   */
  getMapIcon(isCenterIcon = false) {
    return new Icon({
      iconUrl:
        this.locationIsUserIcon && isCenterIcon
          ? 'https://maps.google.com/mapfiles/marker_green.png'
          : 'https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi.png',
      iconSize: [20, 36],
      iconAnchor: [12, 36],
    });
  }

  /**
   * on initial load we need to ensure the map loads correctly
   * @param location
   */
  initializeMap(location) {
    this.map = new Map(this.mapDiv, {
      center: {
        lat: location.lat,
        lng: location.lng,
      },
      zoom: 14,
    }).setView([location.lat, location.lng], 14);

    new TileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 200,
      attribution:
        '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);

    this.__centeredMarker = this.addMapMarker(location, true);
  }

  /**
   * Post initializing of resetting the centre of the map, this is in order to prevent re-initializing
   * @param center
   */
  updateMapCenter(center) {
    if (this.__centeredMarker) {
      this.map.removeLayer(this.__centeredMarker);
    }

    this.map.setView([center.lat, center.lng], 14);
    this.__centeredMarker = this.addMapMarker(center, true);
  }

  /**
   * generic add marker to map functionality
   * @param location
   * @param center
   */
  addMapMarker(location, center = false) {
    const marker = new Marker([location.lat, location.lng], {
      icon: this.getMapIcon(center),
    }).addTo(this.map);

    if (location.title && location.description) {
      marker.bindPopup(`
        <div class="sub-text">${location.title}</div>
        <div>${location.description}</div>
        <div style="margin-top: 16px"><a target="_blank" href="https://www.google.com/maps/dir//${location.lat}, ${location.lng}">Directions</a></div>
      `);
    }

    return marker;
  }
}
